import { Theme, ThemeTokens, ThemeMode } from '../types/theme';

type TokenValue = string | number | Record<string, any>;

// Convert hex color to RGB values
const hexToRgb = (hex: string): string => {
  // Remove # if present
  hex = hex.replace('#', '');
  
  // Parse the hex values
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  
  return `${r}, ${g}, ${b}`;
};

export const generateThemeVariables = (theme: Theme): Record<string, string> => {
  const variables: Record<string, string> = {};

  const processTokens = (obj: Record<string, TokenValue>, prefix: string = '') => {
    Object.entries(obj).forEach(([key, value]) => {
      const cssKey = prefix ? `${prefix}-${key}` : key;
      
      if (typeof value === 'object' && value !== null) {
        processTokens(value as Record<string, TokenValue>, cssKey);
      } else if (typeof value === 'string' && value.startsWith('#')) {
        // If it's a hex color, set both the hex and RGB variables
        variables[`--${cssKey}`] = value;
        variables[`--${cssKey}-rgb`] = hexToRgb(value);
      } else {
        variables[`--${cssKey}`] = String(value);
      }
    });
  };

  // Process all theme tokens except 'mode'
  const { mode, ...tokens } = theme;
  processTokens(tokens);

  return variables;
};

export const applyThemeVariables = (theme: Theme): void => {
  const variables = generateThemeVariables(theme);
  const root = document.documentElement;

  // Apply all theme variables
  Object.entries(variables).forEach(([key, value]) => {
    root.style.setProperty(key, value);
  });

  // Handle dark mode
  if (theme.mode === 'dark') {
    root.classList.add('dark');
  } else {
    root.classList.remove('dark');
  }
};

export const getThemeValue = (theme: Theme, path: string): string | undefined => {
  return path.split('.').reduce((obj: any, key) => obj?.[key], theme);
};

export const createCssVariable = (path: string): string => {
  return `var(--${path.replace(/\./g, '-')})`;
};

export const getInitialThemeMode = (): ThemeMode => {
  const savedTheme = localStorage.getItem('theme') as ThemeMode;
  if (savedTheme) {
    return savedTheme;
  }
  
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
};

export const watchSystemTheme = (callback: (isDark: boolean) => void): () => void => {
  const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
  
  const handleChange = (e: MediaQueryListEvent) => {
    callback(e.matches);
  };
  
  mediaQuery.addEventListener('change', handleChange);
  return () => mediaQuery.removeEventListener('change', handleChange);
}; 